import {
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  format,
  parseISO,
  isSameDay,
  isAfter
} from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { Event } from '../services/hooks/useClassesList';
import { DayView, WeekView, WeekNavigationData } from '../types/Calendar';

/**
 * Obtém os limites de uma semana (domingo a sábado)
 */
export const getWeekBounds = (date: Date) => ({
  start: startOfWeek(date, { weekStartsOn: 0 }), // Domingo
  end: endOfWeek(date, { weekStartsOn: 0 })      // Sábado
});

/**
 * Gera array com os 7 dias da semana a partir de uma data inicial
 */
export const generateWeekDays = (weekStart: Date): DayView[] => {
  const days: DayView[] = [];
  const today = new Date();

  for (let i = 0; i < 7; i++) {
    const date = addDays(weekStart, i);
    days.push({
      date,
      dayName: format(date, 'ccc', { locale: ptBR }).toUpperCase(),
      events: [],
      formattedDate: format(date, 'dd/MM'),
      isToday: isSameDay(date, today)
    });
  }

  return days;
};

/**
 * Agrupa eventos por semana, gerando semanas consecutivas independente de ter eventos
 */
export const groupEventsByWeek = (events: Event[]): WeekView[] => {
  const today = new Date();
  const currentWeekStart = startOfWeek(today, { weekStartsOn: 0 });

  // Gerar 12 semanas consecutivas a partir da semana atual
  const totalWeeksToGenerate = 12;
  const weeks: WeekView[] = [];

  // Filtra apenas eventos futuros e ordena por data
  const futureEvents = events
    .filter(event => {
      const eventDate = parseISO(event.start_date);
      return isAfter(eventDate, today) || isSameDay(eventDate, today);
    })
    .sort((a, b) => parseISO(a.start_date).getTime() - parseISO(b.start_date).getTime());

  // Gera todas as semanas consecutivas
  for (let i = 0; i < totalWeeksToGenerate; i++) {
    const weekStart = addWeeks(currentWeekStart, i);
    const weekEnd = endOfWeek(weekStart, { weekStartsOn: 0 });
    const weekDays = generateWeekDays(weekStart);

    weeks.push({
      weekStart,
      weekEnd,
      weekNumber: i,
      days: weekDays
    });
  }

  // Distribui eventos pelos dias corretos nas semanas correspondentes
  futureEvents.forEach(event => {
    const eventDate = parseISO(event.start_date);

    weeks.forEach(week => {
      week.days.forEach(day => {
        if (isSameDay(day.date, eventDate)) {
          day.events.push(event);
        }
      });
    });
  });

  // Ordena eventos dentro de cada dia por horário
  weeks.forEach(week => {
    week.days.forEach(day => {
      day.events.sort((a, b) => {
        const timeA = parseISO(a.start_date).getTime();
        const timeB = parseISO(b.start_date).getTime();
        return timeA - timeB;
      });
    });
  });

  return weeks;
};

/**
 * Obtém uma semana específica com base na data atual e índice
 */
export const getWeekByIndex = (baseDate: Date, weekIndex: number): WeekView => {
  const targetDate = addWeeks(baseDate, weekIndex);
  const weekBounds = getWeekBounds(targetDate);
  const weekDays = generateWeekDays(weekBounds.start);

  return {
    weekStart: weekBounds.start,
    weekEnd: weekBounds.end,
    weekNumber: weekIndex,
    days: weekDays
  };
};

/**
 * Gera dados de navegação para uma semana específica
 */
export const getWeekNavigationData = (
  currentWeekIndex: number,
  totalWeeks: number,
  currentWeekStart: Date
): WeekNavigationData => {
  return {
    currentWeekIndex,
    totalWeeks,
    hasNext: currentWeekIndex < totalWeeks - 1,
    hasPrevious: currentWeekIndex > 0,
    currentWeekFormatted: `${format(currentWeekStart, 'dd/MM', { locale: ptBR })} - ${format(
      addDays(currentWeekStart, 6),
      'dd/MM',
      { locale: ptBR }
    )}`
  };
};

/**
 * Filtra eventos de uma semana específica baseado nos filtros existentes
 */
export const filterWeekEvents = (
  week: WeekView,
  filteredEvents: Event[]
): WeekView => {
  const filteredWeek: WeekView = {
    ...week,
    days: week.days.map(day => ({
      ...day,
      events: day.events.filter(event =>
        filteredEvents.some(filteredEvent => filteredEvent.id === event.id)
      )
    }))
  };

  return filteredWeek;
};
